import {
  type AccessRequestStatus,
  useAdminAccessRequestsList,
} from "@/features/admin-auth/api/adminAccessRequestsApi";
import { relativeTime } from "@/features/admin-auth/lib/time";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const STATUS_OPTIONS: { value: AccessRequestStatus | ""; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "approved", label: "Aprobados" },
  { value: "rejected", label: "Rechazados" },
  { value: "consumed", label: "Consumidos" },
  { value: "expired", label: "Expirados" },
];

const STATUS_BADGE: Record<AccessRequestStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-700",
  consumed: "bg-neutral-100 text-neutral-500",
  expired: "bg-neutral-200 text-neutral-600",
};

const STATUS_LABEL: Record<AccessRequestStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  consumed: "Consumido",
  expired: "Expirado",
};

const PAGE_SIZE = 20;

export function AdminAccessRequestsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AccessRequestStatus | "">("");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const { data, isPending, error } = useAdminAccessRequestsList({
    status,
    page,
    pageSize: PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const currentLabel = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? "Todos";

  return (
    <>
      <title>Access Requests · Admin HATO</title>

      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h1 className="font-medium text-2xl text-app-ink-1 tracking-tight">Access Requests</h1>

          <div className="relative">
            <button
              type="button"
              onClick={() => setFilterOpen((o) => !o)}
              className="flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-2 text-app-ink-2 text-sm transition-colors hover:bg-app-surface-2"
            >
              {currentLabel}
              <ChevronDown className="h-3.5 w-3.5 text-app-ink-3" />
            </button>
            {filterOpen && (
              <div className="absolute top-full right-0 z-10 mt-1 w-44 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setStatus(opt.value);
                      setPage(1);
                      setFilterOpen(false);
                    }}
                    className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition-colors hover:bg-app-surface-2 ${
                      status === opt.value ? "font-medium text-app-emerald" : "text-app-ink-2"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isPending && (
          <div className="flex justify-center py-16">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-app-border border-t-app-emerald" />
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">
            Error al cargar. Recargá la página.
          </div>
        )}

        {data && data.items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-app-ink-3">
            <p className="text-sm">
              No hay solicitudes
              {status ? ` con estado "${STATUS_LABEL[status as AccessRequestStatus]}"` : "."}
            </p>
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-app-border bg-app-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-app-border border-b bg-app-surface-2">
                  <th className="px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="hidden px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-wider md:table-cell">
                    Teléfono
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="hidden px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-wider sm:table-cell">
                    Creado
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/admin/access-requests/${item.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        navigate(`/admin/access-requests/${item.id}`);
                    }}
                    tabIndex={0}
                    className="cursor-pointer border-app-border border-b transition-colors last:border-0 hover:bg-app-surface-2 focus:outline-none focus-visible:bg-app-surface-2"
                  >
                    <td className="px-4 py-3 text-app-ink-1">{item.email}</td>
                    <td className="hidden px-4 py-3 font-mono text-app-ink-2 text-xs md:table-cell">
                      {item.phone}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status as AccessRequestStatus} />
                    </td>
                    <td className="hidden px-4 py-3 text-app-ink-3 sm:table-cell">
                      {relativeTime(item.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && totalPages > 1 && (
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl border border-app-border px-3 py-2 text-app-ink-2 transition-colors hover:bg-app-surface-2 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </button>
            <span className="text-app-ink-3">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1.5 rounded-xl border border-app-border px-3 py-2 text-app-ink-2 transition-colors hover:bg-app-surface-2 disabled:opacity-40"
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export function StatusBadge({ status }: { status: AccessRequestStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-medium text-xs ${STATUS_BADGE[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}
